import { useState } from 'react';
import { Plus, TriangleAlert } from 'lucide-react';
import { SinDatos, DataState, Badge, Button, FilterTabs, Panel, Pill } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { useEvents, useCreateEvent } from '@/lib/hooks';
import { eventStatus, eventType, type Tone } from '@/lib/status';
import { cn } from '@/lib/cn';
import type { EventStatus, EventType, ExternalEvent } from '@shared/types';

const TYPE_KEYS = Object.keys(eventType) as EventType[];
const STATUS_KEYS = Object.keys(eventStatus) as EventStatus[];
const INPUT = 'h-9 w-full rounded-lg border border-hairline bg-panel px-3 text-sm text-ink-strong placeholder:text-ink-faint focus:border-primary focus:outline-none';

interface FormState {
  name: string;
  type: EventType;
  status: EventStatus;
  description: string;
  periodStart: string;
  periodEnd: string;
  impactEstimate: string;
  categories: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  type: 'aumento_demanda',
  status: 'proximo',
  description: '',
  periodStart: '',
  periodEnd: '',
  impactEstimate: '',
  categories: '',
};

export default function ExternalFactorsPage() {
  const events = useEvents();
  const createEvent = useCreateEvent();
  const [filter, setFilter] = useState('todos');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: Partial<ExternalEvent> = {
      name: form.name,
      type: form.type,
      status: form.status,
      description: form.description,
      periodStart: form.periodStart,
      periodEnd: form.periodEnd,
      impactEstimate: Number(form.impactEstimate) || 0,
      categories: form.categories
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean),
    };
    createEvent.mutate(payload);
    setForm(EMPTY_FORM);
    setShowForm(false);
  };

  return (
    <DataState query={events}>
      {(data) => {
        const tabs = [
          { key: 'todos', label: 'Todos', count: data.counts.total },
          { key: 'activo', label: 'Activo', count: data.counts.activo, tone: 'danger' as Tone },
          { key: 'proximo', label: 'Próximo', count: data.counts.proximo, tone: 'info' as Tone },
          { key: 'completado', label: 'Completado', count: data.counts.completado },
        ];
        const filtered = data.items.filter((ev) => filter === 'todos' || ev.status === filter);
        const activeEvents = data.items.filter((ev) => ev.status === 'activo');

        return (
          <div className="space-y-5">
            {/* El registro de eventos sí funciona y se guarda; lo que NO existe todavía es el
                motor que los use para ajustar la proyección (eso era lo que prometía el copy). */}
            <PageHeader
              title="Factores Externos y Eventos"
              subtitle="Ferias, promociones y quiebres de proveedor que afectan la demanda. Hoy quedan como registro para leer el forecast con contexto; el motor todavía no corrige la proyección con ellos."
              actions={
                <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setShowForm((s) => !s)}>
                  Registrar Evento
                </Button>
              }
            />

            {showForm && (
              <Panel className="p-5">
                <h3 className="text-sm font-semibold text-ink-strong">Nuevo Evento</h3>
                <form onSubmit={handleSubmit} className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <label className="flex flex-col gap-1.5">
                    <span className="text-2xs uppercase tracking-wide text-ink-faint">Nombre</span>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                      placeholder="Ej. Expobelleza 2026"
                      className={INPUT}
                    />
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex flex-col gap-1.5">
                      <span className="text-2xs uppercase tracking-wide text-ink-faint">Tipo</span>
                      <select value={form.type} onChange={(e) => setField('type', e.target.value as EventType)} className={INPUT}>
                        {TYPE_KEYS.map((t) => (
                          <option key={t} value={t}>
                            {eventType[t].label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className="text-2xs uppercase tracking-wide text-ink-faint">Estado</span>
                      <select value={form.status} onChange={(e) => setField('status', e.target.value as EventStatus)} className={INPUT}>
                        {STATUS_KEYS.map((s) => (
                          <option key={s} value={s}>
                            {eventStatus[s].label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <label className="flex flex-col gap-1.5 md:col-span-2">
                    <span className="text-2xs uppercase tracking-wide text-ink-faint">Descripción</span>
                    <textarea
                      value={form.description}
                      onChange={(e) => setField('description', e.target.value)}
                      placeholder="Impacto esperado, canales afectados, contexto…"
                      rows={2}
                      className="w-full rounded-lg border border-hairline bg-panel px-3 py-2 text-sm text-ink-strong placeholder:text-ink-faint focus:border-primary focus:outline-none"
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-2xs uppercase tracking-wide text-ink-faint">Inicio del período</span>
                    <input type="date" value={form.periodStart} onChange={(e) => setField('periodStart', e.target.value)} className={INPUT} />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-2xs uppercase tracking-wide text-ink-faint">Fin del período</span>
                    <input type="date" value={form.periodEnd} onChange={(e) => setField('periodEnd', e.target.value)} className={INPUT} />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-2xs uppercase tracking-wide text-ink-faint">Impacto Estimado (%)</span>
                    <input
                      type="number"
                      value={form.impactEstimate}
                      onChange={(e) => setField('impactEstimate', e.target.value)}
                      placeholder="35"
                      className={INPUT}
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-2xs uppercase tracking-wide text-ink-faint">Categorías (separadas por coma)</span>
                    <input
                      value={form.categories}
                      onChange={(e) => setField('categories', e.target.value)}
                      placeholder="Cuidado Capilar, Fragancias"
                      className={INPUT}
                    />
                  </label>
                  <div className="flex items-center gap-2 md:col-span-2">
                    <Button type="submit" variant="primary" disabled={createEvent.isPending}>
                      {createEvent.isPending ? 'Guardando…' : 'Guardar Evento'}
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>
                      Cancelar
                    </Button>
                  </div>
                </form>
              </Panel>
            )}

            {data.counts.total === 0 ? (
              <SinDatos
                title="No hay eventos cargados"
                detail="Acá se anotan ferias, promociones, quiebres de proveedor o subas de precio: lo que mueve la demanda y no se puede ver en el histórico de venta."
                comoLlenarlo="Se cargan a mano con «Registrar Evento», arriba. Hoy quedan como registro para leer el forecast con contexto; el motor todavía no los usa para corregir la proyección."
              />
            ) : (
              <>
                <FilterTabs items={tabs} value={filter} onChange={setFilter} />

                {/* El banner rojo solo si hay algo activo: con cero anunciaba "0 eventos activos". */}
                {data.counts.activo > 0 && (
                  <div className="surface border-l-2 border-l-danger p-4">
                    <div className="flex items-start gap-3">
                      <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-danger-text" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-ink-strong">
                          {data.counts.activo} eventos activos afectando operaciones
                        </p>
                        {activeEvents.length > 0 && (
                          <p className="mt-1 text-xs text-ink-faint">{activeEvents.map((ev) => ev.name).join(' • ')}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {filtered.map((ev) => (
                    <EventCard key={ev.id} event={ev} />
                  ))}
                </div>
              </>
            )}
          </div>
        );
      }}
    </DataState>
  );
}

function EventCard({ event }: { event: ExternalEvent }) {
  const st = eventStatus[event.status];
  const ty = eventType[event.type];
  const positive = event.impactEstimate > 0;
  return (
    <div className="surface p-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={st.tone}>{st.label}</Badge>
        <Badge tone={ty.tone}>{ty.label}</Badge>
        <span className="text-2xs text-ink-faint">Registrado por {event.registeredBy}</span>
        {event.status === 'activo' && (
          <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-danger-text">
            <span className="h-1.5 w-1.5 rounded-full bg-danger" />
            En curso
          </span>
        )}
      </div>

      <h3 className="mt-3 text-base font-semibold text-ink-strong">{event.name}</h3>
      {event.description && <p className="mt-1 text-xs text-ink-muted">{event.description}</p>}

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <p className="text-2xs uppercase tracking-wide text-ink-faint">Período</p>
          <p className="mt-1 text-sm text-ink-strong">
            {event.periodStart} <span className="text-ink-faint">→</span> {event.periodEnd}
          </p>
        </div>
        <div>
          <p className="text-2xs uppercase tracking-wide text-ink-faint">Impacto Estimado</p>
          <div className="mt-1 flex items-center gap-2">
            <span className={cn('text-lg font-bold', positive ? 'text-success-text' : 'text-danger-text')}>
              {positive ? '+' : ''}
              {event.impactEstimate}%
            </span>
            {event.impactReal != null && (
              <Pill mono>
                {event.impactReal}% (real)
              </Pill>
            )}
          </div>
        </div>
      </div>

      {event.categories.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {event.categories.map((c) => (
            <Pill key={c}>{c}</Pill>
          ))}
        </div>
      )}
    </div>
  );
}
